import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageFadeIn } from '@/components/PageFadeIn';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Download,
  Eye,
  Brain,
  Award,
  Calendar,
  User,
  Filter,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

export function PostTrackingPage() {
  const { projectId, postId } = useParams();
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  usePageTitle('Post Tracking');

  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [trackingData, setTrackingData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    if (postId && company?.id) {
      fetchTrackingData();
    }
  }, [postId, company?.id]);

  const fetchTrackingData = async () => {
    if (!company?.id) return;

    try {
      setLoading(true);

      // SECURITY: Fetch post details and verify it belongs to the current company
      const { data: postData, error: postError } = await supabase
        .from('message_board_posts')
        .select('*')
        .eq('id', postId)
        .eq('company_id', company.id)  // SECURITY: Only fetch if belongs to user's company
        .single();

      if (postError) {
        // Could be not found OR doesn't belong to this company
        console.error('Security: Post not found or access denied');
        toast.error('Post not found or you do not have permission to view it');
        navigate('/posts');
        return;
      }

      setPost(postData);

      // Determine eligible users based on post visibility
      let eligibleUserIds = [];

      if (postData.project_id) {
        // Space-level post - get space members
        const { data: members } = await supabase
          .from('space_members')
          .select('user_id')
          .eq('project_id', postData.project_id);
        eligibleUserIds = members?.map(m => m.user_id) || [];
      } else {
        // Company-wide post - get all company members
        const { data: members } = await supabase
          .from('company_members')
          .select('user_id')
          .eq('company_id', postData.company_id);
        eligibleUserIds = members?.map(m => m.user_id) || [];
      }

      // Fetch all tracking data for this post
      const { data: tracking, error: trackingError } = await supabase
        .from('message_board_post_tracking')
        .select(`
          *,
          user:user_profiles(
            id,
            first_name,
            last_name,
            email,
            avatar_url,
            job_title,
            department
          )
        `)
        .eq('post_id', postId);

      if (trackingError) throw trackingError;

      // Create a map of tracking data by user_id
      const trackingMap = new Map(tracking?.map(t => [t.user_id, t]) || []);

      // Build complete tracking data including users who haven't engaged
      const completeTracking = eligibleUserIds.map(userId => {
        const userTracking = trackingMap.get(userId);

        if (userTracking) {
          return userTracking;
        } else {
          // User hasn't engaged with the post yet
          return {
            user_id: userId,
            post_id: postId,
            user: null, // Will fetch separately
            read_date: null,
            understood_date: null,
            quiz_completed_date: null,
            quiz_score: null,
            quiz_answers: null,
            quiz_time_taken: null
          };
        }
      });

      // Fetch user profiles for those without tracking
      const missingUserIds = completeTracking
        .filter(t => !t.user)
        .map(t => t.user_id);

      if (missingUserIds.length > 0) {
        const { data: users } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, email, avatar_url, job_title, department')
          .in('id', missingUserIds);

        const userMap = new Map(users?.map(u => [u.id, u]) || []);

        completeTracking.forEach(t => {
          if (!t.user && userMap.has(t.user_id)) {
            t.user = userMap.get(t.user_id);
          }
        });
      }

      setTrackingData(completeTracking);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      toast.error('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (tracking) => {
    if (!post) return null;

    const statuses = [];

    if (post.requires_reading && tracking.read_date) {
      statuses.push({ label: 'Read', variant: 'success' });
    } else if (post.requires_reading) {
      statuses.push({ label: 'Not Read', variant: 'secondary' });
    }

    if (post.requires_understanding && tracking.understood_date) {
      statuses.push({ label: 'Understood', variant: 'success' });
    } else if (post.requires_understanding) {
      statuses.push({ label: 'Not Understood', variant: 'secondary' });
    }

    if (post.has_quiz && tracking.quiz_completed_date) {
      const passed = tracking.quiz_score >= 70;
      statuses.push({
        label: `Quiz: ${tracking.quiz_score}%`,
        variant: passed ? 'success' : 'destructive'
      });
    } else if (post.has_quiz) {
      statuses.push({ label: 'Quiz Pending', variant: 'secondary' });
    }

    return statuses;
  };

  const getCompletionStatus = (tracking) => {
    if (!post) return 'pending';

    let required = 0;
    let completed = 0;

    if (post.requires_reading) {
      required++;
      if (tracking.read_date) completed++;
    }

    if (post.requires_understanding) {
      required++;
      if (tracking.understood_date) completed++;
    }

    if (post.has_quiz) {
      required++;
      if (tracking.quiz_completed_date && tracking.quiz_score >= 70) completed++;
    }

    if (required === 0) return 'n/a';
    if (completed === required) return 'complete';
    if (completed > 0) return 'partial';
    return 'pending';
  };

  const filteredAndSortedData = React.useMemo(() => {
    let filtered = trackingData.filter(t => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${t.user?.first_name || ''} ${t.user?.last_name || ''}`.toLowerCase();
        const email = (t.user?.email || '').toLowerCase();
        const jobTitle = (t.user?.job_title || '').toLowerCase();
        const department = (t.user?.department || '').toLowerCase();

        if (!fullName.includes(searchLower) &&
            !email.includes(searchLower) &&
            !jobTitle.includes(searchLower) &&
            !department.includes(searchLower)) {
          return false;
        }
      }

      // Status filter
      if (filterStatus !== 'all') {
        const status = getCompletionStatus(t);
        if (status !== filterStatus) return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return `${a.user?.first_name} ${a.user?.last_name}`.localeCompare(
            `${b.user?.first_name} ${b.user?.last_name}`
          );
        case 'status':
          return getCompletionStatus(b).localeCompare(getCompletionStatus(a));
        case 'score':
          return (b.quiz_score || 0) - (a.quiz_score || 0);
        case 'date':
          const aDate = a.quiz_completed_date || a.understood_date || a.read_date;
          const bDate = b.quiz_completed_date || b.understood_date || b.read_date;
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          return new Date(bDate) - new Date(aDate);
        default:
          return 0;
      }
    });

    return filtered;
  }, [trackingData, searchTerm, filterStatus, sortBy, post]);

  const exportToCSV = () => {
    const headers = [
      'Name',
      'Email',
      'Department',
      'Job Title',
      'Status',
      'Read Date',
      'Understood Date',
      'Quiz Completed Date',
      'Quiz Score',
      'Time Taken (seconds)'
    ];

    const rows = filteredAndSortedData.map(t => [
      `${t.user?.first_name || ''} ${t.user?.last_name || ''}`,
      t.user?.email || '',
      t.user?.department || '',
      t.user?.job_title || '',
      getCompletionStatus(t),
      t.read_date ? format(new Date(t.read_date), 'yyyy-MM-dd HH:mm:ss') : '',
      t.understood_date ? format(new Date(t.understood_date), 'yyyy-MM-dd HH:mm:ss') : '',
      t.quiz_completed_date ? format(new Date(t.quiz_completed_date), 'yyyy-MM-dd HH:mm:ss') : '',
      t.quiz_score !== null ? t.quiz_score : '',
      t.quiz_time_taken || ''
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `post-tracking-${postId}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Export complete');
  };

  const stats = React.useMemo(() => {
    if (!post || trackingData.length === 0) return null;

    const total = trackingData.length;
    const read = trackingData.filter(t => t.read_date).length;
    const understood = trackingData.filter(t => t.understood_date).length;
    const quizCompleted = trackingData.filter(t => t.quiz_completed_date).length;
    const quizPassed = trackingData.filter(t => t.quiz_score >= 70).length;
    const avgScore = quizCompleted > 0
      ? Math.round(trackingData.reduce((sum, t) => sum + (t.quiz_score || 0), 0) / quizCompleted)
      : 0;

    return {
      total,
      read,
      understood,
      quizCompleted,
      quizPassed,
      avgScore,
      complete: trackingData.filter(t => getCompletionStatus(t) === 'complete').length,
      partial: trackingData.filter(t => getCompletionStatus(t) === 'partial').length,
      pending: trackingData.filter(t => getCompletionStatus(t) === 'pending').length
    };
  }, [trackingData, post]);

  if (loading) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  if (!post) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <p className="text-muted-foreground">Post not found</p>
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3>
      <ToolHeader
        toolName="Posts"
        breadcrumbs={[
          {
            name: post.title,
            onClick: () => navigate(projectId
              ? `/space/${projectId}/message-board/${postId}`
              : `/posts/${postId}`
            ),
            onToolClick: () => navigate(projectId
              ? `/space/${projectId}/message-board`
              : '/posts'
            )
          },
          { name: 'Tracking' }
        ]}
      />
      <PageFadeIn>
        <div className='max-w-7xl mx-auto px-6 py-6 space-y-6'>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Total Eligible
                  </CardDescription>
                  <CardTitle className="text-3xl">{stats.total}</CardTitle>
                </CardHeader>
              </Card>

              {post.requires_reading && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Read
                    </CardDescription>
                    <CardTitle className="text-3xl">
                      {stats.read}
                      <span className="text-base text-muted-foreground ml-2">
                        / {stats.total}
                      </span>
                    </CardTitle>
                  </CardHeader>
                </Card>
              )}

              {post.requires_understanding && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      Understood
                    </CardDescription>
                    <CardTitle className="text-3xl">
                      {stats.understood}
                      <span className="text-base text-muted-foreground ml-2">
                        / {stats.total}
                      </span>
                    </CardTitle>
                  </CardHeader>
                </Card>
              )}

              {post.has_quiz && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription className="flex items-center gap-2">
                      <Award className="h-4 w-4" />
                      Quiz (Avg Score)
                    </CardDescription>
                    <CardTitle className="text-3xl">
                      {stats.avgScore}%
                      <span className="text-base text-muted-foreground ml-2">
                        ({stats.quizPassed}/{stats.quizCompleted})
                      </span>
                    </CardTitle>
                  </CardHeader>
                </Card>
              )}
            </div>
          )}

          {/* Filters and Actions */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex-1 w-full sm:w-auto">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, email, department..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[160px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="status">Status</SelectItem>
                      {post.has_quiz && <SelectItem value="score">Quiz Score</SelectItem>}
                      <SelectItem value="date">Last Activity</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button onClick={exportToCSV} variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Status</TableHead>
                      {post.requires_reading && <TableHead>Read</TableHead>}
                      {post.requires_understanding && <TableHead>Understood</TableHead>}
                      {post.has_quiz && (
                        <>
                          <TableHead>Quiz Score</TableHead>
                          <TableHead>Completed</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                          No tracking data found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAndSortedData.map((tracking) => {
                        const status = getCompletionStatus(tracking);
                        const isExpanded = expandedRows.has(tracking.user_id);
                        const hasQuizAnswers = post.has_quiz && tracking.quiz_answers && tracking.quiz_answers.length > 0;

                        return (
                          <React.Fragment key={tracking.user_id}>
                            <TableRow className={hasQuizAnswers ? 'cursor-pointer hover:bg-muted/50' : ''}>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  {hasQuizAnswers && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => {
                                        const newExpanded = new Set(expandedRows);
                                        if (isExpanded) {
                                          newExpanded.delete(tracking.user_id);
                                        } else {
                                          newExpanded.add(tracking.user_id);
                                        }
                                        setExpandedRows(newExpanded);
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={tracking.user?.avatar_url} />
                                    <AvatarFallback>
                                      {tracking.user?.first_name?.[0]}{tracking.user?.last_name?.[0]}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="font-medium">
                                      {tracking.user?.first_name} {tracking.user?.last_name}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {tracking.user?.email}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {tracking.user?.department || '—'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {tracking.user?.job_title || ''}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  status === 'complete' ? 'success' :
                                  status === 'partial' ? 'warning' :
                                  status === 'pending' ? 'secondary' : 'outline'
                                }
                              >
                                {status === 'complete' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                                {status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </Badge>
                            </TableCell>
                            {post.requires_reading && (
                              <TableCell>
                                {tracking.read_date ? (
                                  <div className="text-sm">
                                    <div className="flex items-center gap-1 text-green-600">
                                      <CheckCircle2 className="h-3 w-3" />
                                      Yes
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {format(new Date(tracking.read_date), 'MMM d, HH:mm')}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <XCircle className="h-3 w-3" />
                                    No
                                  </div>
                                )}
                              </TableCell>
                            )}
                            {post.requires_understanding && (
                              <TableCell>
                                {tracking.understood_date ? (
                                  <div className="text-sm">
                                    <div className="flex items-center gap-1 text-green-600">
                                      <CheckCircle2 className="h-3 w-3" />
                                      Yes
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {format(new Date(tracking.understood_date), 'MMM d, HH:mm')}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <XCircle className="h-3 w-3" />
                                    No
                                  </div>
                                )}
                              </TableCell>
                            )}
                            {post.has_quiz && (
                              <>
                                <TableCell>
                                  {tracking.quiz_score !== null ? (
                                    <Badge
                                      variant={tracking.quiz_score >= 70 ? 'success' : 'destructive'}
                                    >
                                      {tracking.quiz_score}%
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {tracking.quiz_completed_date ? (
                                    <div className="text-sm">
                                      <div>{format(new Date(tracking.quiz_completed_date), 'MMM d, yyyy')}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {format(new Date(tracking.quiz_completed_date), 'HH:mm')}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                              </>
                            )}
                          </TableRow>

                          {/* Expanded Quiz Answers Row */}
                          {isExpanded && hasQuizAnswers && (
                            <TableRow>
                              <TableCell colSpan={7} className="bg-muted/30 p-6">
                                <div className="space-y-4">
                                  <div className="flex items-center gap-2 mb-4">
                                    <Award className="h-5 w-5 text-primary" />
                                    <h4 className="font-semibold text-lg">Quiz Answers</h4>
                                    <Badge variant={
                                      tracking.quiz_answers.filter(a => a.isCorrect).length / tracking.quiz_answers.length >= 0.7
                                        ? 'success'
                                        : 'warning'
                                    }>
                                      {tracking.quiz_answers.filter(a => a.isCorrect).length}/{tracking.quiz_answers.length} Correct
                                    </Badge>
                                  </div>
                                  {tracking.quiz_answers.map((answer, idx) => {
                                    if (!answer || !answer.question) return null;

                                    const isCorrect = answer.isCorrect;

                                    return (
                                      <div key={idx} className="bg-background rounded-lg border p-4 space-y-3">
                                        <div className="flex items-start justify-between gap-4">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                              <span className="font-semibold text-sm">Question {idx + 1}</span>
                                              <Badge variant={isCorrect ? 'success' : 'destructive'}>
                                                {isCorrect ? (
                                                  <>
                                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                                    Correct
                                                  </>
                                                ) : (
                                                  <>
                                                    <XCircle className="h-3 w-3 mr-1" />
                                                    Incorrect
                                                  </>
                                                )}
                                              </Badge>
                                            </div>
                                            <p className="text-sm font-medium mb-3">{answer.question}</p>

                                            <div className="space-y-2">
                                              <div
                                                className={`p-3 rounded-md border-2 text-sm ${
                                                  isCorrect
                                                    ? 'bg-green-50 border-green-500 text-green-900'
                                                    : 'bg-red-50 border-red-500 text-red-900'
                                                }`}
                                              >
                                                <div className="flex items-start gap-2">
                                                  <span className="font-semibold mt-0.5">
                                                    {isCorrect ? '✓' : '✗'}
                                                  </span>
                                                  <div className="flex-1">
                                                    <div className="font-medium">User's Answer:</div>
                                                    <div>{answer.userAnswer || 'No answer provided'}</div>
                                                  </div>
                                                </div>
                                              </div>

                                              {!isCorrect && (
                                                <div className="p-3 rounded-md border-2 bg-green-50 border-green-300 text-green-800 text-sm">
                                                  <div className="flex items-start gap-2">
                                                    <span className="font-semibold text-green-600 mt-0.5">✓</span>
                                                    <div className="flex-1">
                                                      <div className="font-medium">Correct Answer:</div>
                                                      <div>{answer.correctAnswer}</div>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageFadeIn>
    </AppShell3>
  );
}
